/**
 * Conversion Service
 *
 * API service for Lead Conversion & Referral Fee operations.
 * Handles conversion submissions, document uploads, approvals,
 * and referral fee payment workflow.
 *
 * Base URL: /api/leadflow/conversions
 *
 * Flow:
 * 1. Collaborator submits conversion with proof documents
 * 2. Owner reviews and approves/rejects
 * 3. Upon approval, referral fee is transferred from collaborator to owner
 */

import axiosInstance from '../config/axios.config';

// ================================
// CONSTANTS & CONFIGURATIONS
// ================================

/**
 * Conversion submission status options
 */
export const CONVERSION_STATUS = {
    draft: {
        value: 'draft',
        label: 'Draft',
        color: '#6B7280',
        bgColor: '#F3F4F6',
        icon: 'pi-file-edit',
        severity: 'secondary',
        description: 'Saved but not submitted'
    },
    pending_approval: {
        value: 'pending_approval',
        label: 'Pending Approval',
        color: '#F59E0B',
        bgColor: '#FEF3C7',
        icon: 'pi-clock',
        severity: 'warning',
        description: 'Awaiting owner review'
    },
    clarification_requested: {
        value: 'clarification_requested',
        label: 'Clarification Needed',
        color: '#8B5CF6',
        bgColor: '#EDE9FE',
        icon: 'pi-question-circle',
        severity: 'info',
        description: 'Owner needs more information'
    },
    approved: {
        value: 'approved',
        label: 'Approved',
        color: '#22C55E',
        bgColor: '#DCFCE7',
        icon: 'pi-check-circle',
        severity: 'success',
        description: 'Conversion approved, payment processed'
    },
    rejected: {
        value: 'rejected',
        label: 'Rejected',
        color: '#EF4444',
        bgColor: '#FEE2E2',
        icon: 'pi-times-circle',
        severity: 'danger',
        description: 'Conversion rejected by owner'
    },
    disputed: {
        value: 'disputed',
        label: 'Disputed',
        color: '#F97316',
        bgColor: '#FFEDD5',
        icon: 'pi-exclamation-triangle',
        severity: 'warning',
        description: 'Dispute raised'
    },
    cancelled: {
        value: 'cancelled',
        label: 'Cancelled',
        color: '#6B7280',
        bgColor: '#F3F4F6',
        icon: 'pi-ban',
        severity: 'secondary',
        description: 'Submission cancelled'
    }
};

/**
 * Payment status options
 */
export const PAYMENT_STATUS = {
    pending: {
        value: 'pending',
        label: 'Pending',
        color: '#F59E0B',
        bgColor: '#FEF3C7',
        icon: 'pi-clock',
        description: 'Awaiting approval'
    },
    processing: {
        value: 'processing',
        label: 'Processing',
        color: '#3B82F6',
        bgColor: '#DBEAFE',
        icon: 'pi-spin pi-spinner',
        description: 'Payment in progress'
    },
    completed: {
        value: 'completed',
        label: 'Completed',
        color: '#22C55E',
        bgColor: '#DCFCE7',
        icon: 'pi-check-circle',
        description: 'Payment successful'
    },
    failed: {
        value: 'failed',
        label: 'Failed',
        color: '#EF4444',
        bgColor: '#FEE2E2',
        icon: 'pi-times-circle',
        description: 'Payment failed'
    },
    refunded: {
        value: 'refunded',
        label: 'Refunded',
        color: '#8B5CF6',
        bgColor: '#EDE9FE',
        icon: 'pi-undo',
        description: 'Payment refunded'
    }
};

/**
 * Document type options
 */
export const DOCUMENT_TYPES = {
    sanction_letter: {
        value: 'sanction_letter',
        label: 'Sanction Letter',
        icon: 'pi-file',
        required: true,
        description: "Bank's loan sanction letter"
    },
    disbursement_proof: {
        value: 'disbursement_proof',
        label: 'Disbursement Proof',
        icon: 'pi-check-square',
        required: true,
        description: 'Proof of fund disbursement'
    },
    bank_statement: {
        value: 'bank_statement',
        label: 'Bank Statement',
        icon: 'pi-book',
        required: false,
        description: 'Customer bank statement showing credit'
    },
    commission_receipt: {
        value: 'commission_receipt',
        label: 'Commission Receipt',
        icon: 'pi-receipt',
        required: false,
        description: 'Proof of commission from lender'
    },
    customer_acknowledgment: {
        value: 'customer_acknowledgment',
        label: 'Customer Acknowledgment',
        icon: 'pi-user-edit',
        required: false,
        description: 'Customer confirmation'
    },
    other: {
        value: 'other',
        label: 'Other',
        icon: 'pi-paperclip',
        required: false,
        description: 'Additional supporting documents'
    }
};

/**
 * Required document types
 */
export const REQUIRED_DOCUMENT_TYPES = ['sanction_letter', 'disbursement_proof'];

/**
 * Rejection reason options
 */
export const REJECTION_REASONS = [
    { value: 'invalid_documents', label: 'Invalid Documents', description: 'Uploaded documents are invalid or unreadable' },
    { value: 'incorrect_amount', label: 'Incorrect Amount', description: 'Disbursement or commission amount is incorrect' },
    { value: 'missing_information', label: 'Missing Information', description: 'Required information is missing' },
    { value: 'fraudulent', label: 'Suspected Fraud', description: 'Documents or information appear fraudulent' },
    { value: 'other', label: 'Other', description: 'Other reason (specify in notes)' }
];

/**
 * Document verification status options
 */
export const VERIFICATION_STATUS = {
    pending: { value: 'pending', label: 'Pending Review', color: '#F59E0B', icon: 'pi-clock' },
    verified: { value: 'verified', label: 'Verified', color: '#22C55E', icon: 'pi-check-circle' },
    rejected: { value: 'rejected', label: 'Rejected', color: '#EF4444', icon: 'pi-times-circle' },
    flagged: { value: 'flagged', label: 'Flagged', color: '#F97316', icon: 'pi-flag' }
};

// ================================
// API SERVICE METHODS
// ================================

export const conversionService = {
    // ================================
    // SUBMISSION MANAGEMENT
    // ================================

    /**
     * Create a new conversion submission for a collaboration
     * @param {string} collaborationId - Collaboration UUID
     * @param {Object} data - Conversion data
     * @param {number} data.loan_amount_disbursed - Disbursed loan amount
     * @param {number} data.commission_received - Commission received from bank
     * @param {string} data.disbursement_date - Date of disbursement (YYYY-MM-DD)
     * @param {string} data.lender_name - Name of the lending bank
     * @param {string} data.lender_branch - Branch name
     * @param {string} data.loan_account_number - Loan account number
     * @returns {Promise<Object>} Created submission
     */
    createSubmission: async (collaborationId, data) => {
        try {
            const response = await axiosInstance.post(
                `/leadflow/collaborations/${collaborationId}/conversion`,
                data
            );
            return response.data;
        } catch (error) {
            console.error('conversionService.createSubmission:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get current conversion submission for a collaboration
     * @param {string} collaborationId - Collaboration UUID
     * @returns {Promise<Object>} Current submission or null
     */
    getSubmissionForCollaboration: async (collaborationId) => {
        try {
            const response = await axiosInstance.get(
                `/leadflow/collaborations/${collaborationId}/conversion`
            );
            return response.data;
        } catch (error) {
            console.error('conversionService.getSubmissionForCollaboration:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get conversion submission details
     * @param {string} submissionId - Submission UUID
     * @returns {Promise<Object>} Submission details with documents
     */
    getSubmissionDetails: async (submissionId) => {
        try {
            const response = await axiosInstance.get(`/leadflow/conversions/${submissionId}`);
            return response.data;
        } catch (error) {
            console.error('conversionService.getSubmissionDetails:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Update a conversion submission (draft or clarification)
     * @param {string} submissionId - Submission UUID
     * @param {Object} data - Fields to update
     * @returns {Promise<Object>} Updated submission
     */
    updateSubmission: async (submissionId, data) => {
        try {
            const response = await axiosInstance.put(
                `/leadflow/conversions/${submissionId}`,
                data
            );
            return response.data;
        } catch (error) {
            console.error('conversionService.updateSubmission:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Cancel a conversion submission
     * @param {string} submissionId - Submission UUID
     * @returns {Promise<Object>} Cancellation result
     */
    cancelSubmission: async (submissionId) => {
        try {
            const response = await axiosInstance.delete(`/leadflow/conversions/${submissionId}`);
            return response.data;
        } catch (error) {
            console.error('conversionService.cancelSubmission:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Submit conversion for approval
     * @param {string} submissionId - Submission UUID
     * @returns {Promise<Object>} Submission result
     */
    submitForApproval: async (submissionId) => {
        try {
            const response = await axiosInstance.post(
                `/leadflow/conversions/${submissionId}/submit`
            );
            return response.data;
        } catch (error) {
            console.error('conversionService.submitForApproval:', error);
            throw error.response?.data || error;
        }
    },

    // ================================
    // DOCUMENT MANAGEMENT
    // ================================

    /**
     * Upload a document to the conversion submission
     * @param {string} submissionId - Submission UUID
     * @param {File} file - File to upload
     * @param {string} documentType - Document type (sanction_letter, disbursement_proof, etc.)
     * @param {string} documentLabel - Custom label for 'other' type
     * @returns {Promise<Object>} Uploaded document
     */
    uploadDocument: async (submissionId, file, documentType, documentLabel = null) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('document_type', documentType);
            if (documentLabel) {
                formData.append('document_label', documentLabel);
            }

            const response = await axiosInstance.post(
                `/leadflow/conversions/${submissionId}/documents`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            return response.data;
        } catch (error) {
            console.error('conversionService.uploadDocument:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Delete a document from the conversion submission
     * @param {string} submissionId - Submission UUID
     * @param {string} documentId - Document UUID
     * @returns {Promise<Object>} Deletion result
     */
    deleteDocument: async (submissionId, documentId) => {
        try {
            const response = await axiosInstance.delete(
                `/leadflow/conversions/${submissionId}/documents/${documentId}`
            );
            return response.data;
        } catch (error) {
            console.error('conversionService.deleteDocument:', error);
            throw error.response?.data || error;
        }
    },

    // ================================
    // OWNER REVIEW ACTIONS
    // ================================

    /**
     * Approve a conversion submission
     * @param {string} submissionId - Submission UUID
     * @returns {Promise<Object>} Approval result with payment details
     */
    approveConversion: async (submissionId) => {
        try {
            const response = await axiosInstance.post(
                `/leadflow/conversions/${submissionId}/approve`
            );
            return response.data;
        } catch (error) {
            console.error('conversionService.approveConversion:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Reject a conversion submission
     * @param {string} submissionId - Submission UUID
     * @param {string} reason - Rejection reason (from REJECTION_REASONS)
     * @param {string} notes - Additional notes
     * @returns {Promise<Object>} Rejection result
     */
    rejectConversion: async (submissionId, reason, notes = null) => {
        try {
            const response = await axiosInstance.post(
                `/leadflow/conversions/${submissionId}/reject`,
                { reason, notes }
            );
            return response.data;
        } catch (error) {
            console.error('conversionService.rejectConversion:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Request clarification from collaborator
     * @param {string} submissionId - Submission UUID
     * @param {string} notes - What clarification is needed
     * @returns {Promise<Object>} Request result
     */
    requestClarification: async (submissionId, notes) => {
        try {
            const response = await axiosInstance.post(
                `/leadflow/conversions/${submissionId}/clarification`,
                { notes }
            );
            return response.data;
        } catch (error) {
            console.error('conversionService.requestClarification:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Respond to clarification request (as collaborator)
     * @param {string} submissionId - Submission UUID
     * @param {string} response - Response to the clarification request
     * @returns {Promise<Object>} Response result
     */
    respondToClarification: async (submissionId, responseText) => {
        try {
            const response = await axiosInstance.post(
                `/leadflow/conversions/${submissionId}/respond`,
                { response: responseText }
            );
            return response.data;
        } catch (error) {
            console.error('conversionService.respondToClarification:', error);
            throw error.response?.data || error;
        }
    },

    // ================================
    // QUERY METHODS
    // ================================

    /**
     * Get pending approvals for the authenticated owner
     * @param {Object} params - Query parameters
     * @param {number} params.page - Page number (default: 1)
     * @param {number} params.per_page - Items per page (default: 10)
     * @param {string} params.search - Search by collaborator name or lead name
     * @param {string} params.lender_name - Filter by lender name
     * @param {string} params.date_from - Filter by submitted date (from)
     * @param {string} params.date_to - Filter by submitted date (to)
     * @param {string} params.loan_type - Filter by loan/product type
     * @returns {Promise<Object>} List of pending conversions with pagination
     */
    getPendingApprovals: async (params = {}) => {
        try {
            const response = await axiosInstance.get('/leadflow/conversions/pending', { params });
            return response.data;
        } catch (error) {
            console.error('conversionService.getPendingApprovals:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get approved/converted conversions for lead owner
     * @param {Object} params - Query parameters
     * @param {number} params.page - Page number (default: 1)
     * @param {number} params.per_page - Items per page (default: 10)
     * @param {string} params.search - Search by collaborator name or lead name
     * @param {string} params.lender_name - Filter by lender name
     * @param {string} params.date_from - Filter by reviewed/approved date (from)
     * @param {string} params.date_to - Filter by reviewed/approved date (to)
     * @param {string} params.loan_type - Filter by loan/product type
     * @returns {Promise<Object>} List of approved conversion submissions with pagination
     */
    getApprovedConversions: async (params = {}) => {
        try {
            const response = await axiosInstance.get('/leadflow/conversions/approved', { params });
            return response.data;
        } catch (error) {
            console.error('conversionService.getApprovedConversions:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get my submissions as a collaborator
     * @returns {Promise<Object>} List of my conversion submissions
     */
    getMySubmissions: async () => {
        try {
            const response = await axiosInstance.get('/leadflow/conversions/my-submissions');
            return response.data;
        } catch (error) {
            console.error('conversionService.getMySubmissions:', error);
            throw error.response?.data || error;
        }
    }
};

// ================================
// UTILITY FUNCTIONS
// ================================

/**
 * Get status configuration
 * @param {string} status - Status value
 * @returns {Object} Status config with color, label, icon
 */
export const getStatusConfig = (status) => {
    return CONVERSION_STATUS[status?.toLowerCase()] || {
        value: status,
        label: status || 'Unknown',
        color: '#6B7280',
        bgColor: '#F3F4F6',
        icon: 'pi-question',
        severity: 'secondary',
        description: 'Unknown status'
    };
};

/**
 * Get payment status configuration
 * @param {string} status - Payment status value
 * @returns {Object} Status config
 */
export const getPaymentStatusConfig = (status) => {
    return PAYMENT_STATUS[status?.toLowerCase()] || {
        value: status,
        label: status || 'Unknown',
        color: '#6B7280',
        bgColor: '#F3F4F6',
        icon: 'pi-question',
        description: 'Unknown status'
    };
};

/**
 * Get document type configuration
 * @param {string} type - Document type value
 * @returns {Object} Type config with icon, label, description
 */
export const getDocumentTypeConfig = (type) => {
    return DOCUMENT_TYPES[type] || {
        value: type,
        label: type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown',
        icon: 'pi-file',
        required: false,
        description: 'Unknown document type'
    };
};

/**
 * Get verification status configuration
 * @param {string} status - Verification status value
 * @returns {Object} Status config
 */
export const getVerificationStatusConfig = (status) => {
    return VERIFICATION_STATUS[status?.toLowerCase()] || {
        value: status,
        label: status || 'Pending',
        color: '#6B7280',
        icon: 'pi-question'
    };
};

/**
 * Get rejection reason configuration
 * @param {string} reason - Rejection reason value
 * @returns {Object} Reason config
 */
export const getRejectionReasonConfig = (reason) => {
    return REJECTION_REASONS.find(r => r.value === reason) || {
        value: reason,
        label: reason?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown',
        description: 'Unknown reason'
    };
};

/**
 * Check if a submission has all required documents
 * @param {Array} documents - Array of document objects
 * @returns {Object} { hasAll: boolean, missing: string[] }
 */
export const checkRequiredDocuments = (documents = []) => {
    const uploadedTypes = documents
        .filter(d => !d.deleted_at)
        .map(d => d.type || d.document_type);

    const missing = REQUIRED_DOCUMENT_TYPES.filter(type => !uploadedTypes.includes(type));

    return {
        hasAll: missing.length === 0,
        missing,
        missingLabels: missing.map(type => DOCUMENT_TYPES[type]?.label || type)
    };
};

/**
 * Format currency in Indian Rupee format
 * @param {number} amount - Amount in INR
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';

    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 2
    }).format(amount);
};

/**
 * Format large amounts with Lakhs/Crores
 * @param {number} amount - Amount in INR
 * @returns {string} Formatted amount
 */
export const formatLargeAmount = (amount) => {
    if (!amount) return '-';

    if (amount >= 10000000) {
        return `${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
        return `${(amount / 100000).toFixed(2)} L`;
    } else if (amount >= 1000) {
        return `${(amount / 1000).toFixed(0)}K`;
    }
    return formatCurrency(amount);
};

/**
 * Calculate referral fee based on loan amount disbursed and owner's percentage
 * Formula: loan_amount_disbursed × (owner_percentage / 100)
 * @param {number} loanAmountDisbursed - Loan amount disbursed
 * @param {number} percentage - Owner's percentage
 * @returns {number} Calculated referral fee
 */
export const calculateReferralFee = (loanAmountDisbursed, percentage) => {
    if (!loanAmountDisbursed || !percentage) return 0;
    return Math.round((loanAmountDisbursed * (percentage / 100)) * 100) / 100;
};

/**
 * Format percentage with up to 3 decimal places (removes trailing zeros)
 * @param {number} percentage - Percentage value
 * @returns {string} Formatted percentage string (e.g., "3.5%", "12.125%", "5%")
 */
export const formatPercentage = (percentage) => {
    if (percentage === null || percentage === undefined) return '-';
    // Format to 3 decimal places, then parse to remove trailing zeros
    return `${parseFloat(parseFloat(percentage).toFixed(3))}%`;
};

/**
 * Format file size
 * @param {number} bytes - File size in bytes
 * @returns {string} Human readable file size
 */
export const formatFileSize = (bytes) => {
    if (!bytes) return '-';

    const units = ['B', 'KB', 'MB', 'GB'];
    let unitIndex = 0;
    let size = bytes;

    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
    }

    return `${size.toFixed(unitIndex > 0 ? 1 : 0)} ${units[unitIndex]}`;
};

/**
 * Format date for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
export const formatDate = (dateString) => {
    if (!dateString) return '-';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });
};

/**
 * Format datetime for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted datetime
 */
export const formatDateTime = (dateString) => {
    if (!dateString) return '-';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

/**
 * Format relative time
 * @param {string} dateString - ISO date string
 * @returns {string} Relative time string
 */
export const formatTimeAgo = (dateString) => {
    if (!dateString) return '-';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

    return formatDate(dateString);
};

/**
 * Get file icon based on mime type
 * @param {string} mimeType - File MIME type
 * @returns {string} PrimeIcons class
 */
export const getFileIcon = (mimeType) => {
    if (!mimeType) return 'pi-file';

    if (mimeType.includes('pdf')) return 'pi-file-pdf';
    if (mimeType.includes('image')) return 'pi-image';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'pi-file-word';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'pi-file-excel';

    return 'pi-file';
};

/**
 * Check if file is viewable in browser
 * @param {string} mimeType - File MIME type
 * @returns {boolean} Whether file can be previewed
 */
export const isFileViewable = (mimeType) => {
    if (!mimeType) return false;
    return mimeType.includes('pdf') || mimeType.includes('image');
};

/**
 * Determine if user can perform action on submission
 * @param {Object} submission - Submission object
 * @param {string} action - Action to check
 * @param {boolean} isOwner - Whether current user is owner
 * @param {boolean} isSubmitter - Whether current user is submitter
 * @returns {boolean} Whether action is allowed
 */
export const canPerformAction = (submission, action, isOwner, isSubmitter) => {
    if (!submission) return false;

    const status = submission.status;

    switch (action) {
        case 'edit':
            return isSubmitter && ['draft', 'clarification_requested'].includes(status);

        case 'submit':
            return isSubmitter && status === 'draft';

        case 'cancel':
            return isSubmitter && ['draft', 'pending_approval', 'clarification_requested'].includes(status);

        case 'upload_document':
            return isSubmitter && ['draft', 'clarification_requested'].includes(status);

        case 'approve':
            return isOwner && status === 'pending_approval';

        case 'reject':
            return isOwner && status === 'pending_approval';

        case 'request_clarification':
            return isOwner && status === 'pending_approval';

        case 'respond_clarification':
            return isSubmitter && status === 'clarification_requested';

        default:
            return false;
    }
};

/**
 * Get conversion timeline steps
 * @param {Object} submission - Submission object
 * @returns {Array} Timeline steps with status
 */
export const getConversionTimeline = (submission) => {
    if (!submission) return [];

    const steps = [
        {
            id: 'created',
            label: 'Submission Created',
            date: submission.created_at,
            completed: true,
            icon: 'pi-plus-circle'
        },
        {
            id: 'documents',
            label: 'Documents Uploaded',
            completed: submission.has_required_documents,
            icon: 'pi-upload'
        },
        {
            id: 'submitted',
            label: 'Submitted for Approval',
            date: submission.submitted_at,
            completed: !!submission.submitted_at,
            icon: 'pi-send'
        },
        {
            id: 'reviewed',
            label: 'Owner Review',
            date: submission.reviewed_at,
            completed: ['approved', 'rejected'].includes(submission.status),
            icon: 'pi-eye',
            status: submission.status
        }
    ];

    if (submission.status === 'approved') {
        steps.push({
            id: 'payment',
            label: 'Payment Processed',
            date: submission.payment_completed_at,
            completed: submission.payment_status === 'completed',
            icon: 'pi-wallet'
        });
    }

    return steps;
};

export default conversionService;
